import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Send, Bot, User, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ReceivableChatProps {
  statementId: string;
  dealerName?: string;
  lenderName?: string;
}

export function ReceivableChat({ statementId, dealerName, lenderName }: ReceivableChatProps) {
  const { t } = useTranslation(["floorplan", "common"]);
  const STARTER_QUESTIONS = [
    t("floorplan:chat.starters.totalInterest"),
    t("floorplan:chat.starters.highestBalance"),
    t("floorplan:chat.starters.over60Days"),
    t("floorplan:chat.starters.feesCharged"),
    t("floorplan:chat.starters.effectiveApr"),
  ];
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserBubbleRef = useRef<HTMLDivElement>(null);
  const streamingBubbleRef = useRef<HTMLDivElement>(null);
  const hasScrolledToStreamRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  // Scroll to the bottom only when history loads or the user sends a message
  // (so their just-sent bubble is visible). While the assistant is streaming
  // we intentionally do NOT keep pinning to the bottom — instead, when the
  // answer starts, we scroll the assistant's bubble to the top of the chat
  // area so the user reads from the beginning (same behavior as DEAN chat).
  const userMessageCount = messages.filter((m) => m.role === "user").length;
  useEffect(() => {
    if (isLoadingHistory) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isLoadingHistory, userMessageCount]);

  const streamingText =
    isStreaming && messages[messages.length - 1]?.role === "assistant"
      ? messages[messages.length - 1].content
      : "";
  useEffect(() => {
    if (!isStreaming) {
      hasScrolledToStreamRef.current = false;
      return;
    }
    if (streamingText && !hasScrolledToStreamRef.current) {
      hasScrolledToStreamRef.current = true;
      const container = scrollRef.current;
      const bubble = streamingBubbleRef.current;
      if (container && bubble) {
        // Keep up to ~3 lines of the user's question visible above the answer.
        const questionEl = lastUserBubbleRef.current;
        const questionPeek = questionEl
          ? Math.min(questionEl.offsetHeight, 84) + 12
          : 8;
        const top =
          bubble.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          questionPeek;
        container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
      }
    }
  }, [isStreaming, streamingText]);

  useEffect(() => {
    setMessages([]);
    async function loadHistory() {
      setIsLoadingHistory(true);
      try {
        const res = await fetch(`/api/chat/${statementId}/history`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setMessages(Array.isArray(data.messages) ? data.messages : []);
        } else {
          setMessages([]);
        }
      } catch {
        setMessages([]);
      } finally {
        setIsLoadingHistory(false);
      }
    }
    loadHistory();
  }, [statementId]);

  async function clearChat() {
    if (isStreaming) return;
    try {
      await fetch(`/api/chat/${statementId}`, {
        method: "DELETE",
        credentials: "include",
      });
      setMessages([]);
    } catch {
      toast({ title: t("floorplan:chat.clearFailed"), variant: "destructive" });
    }
  }

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsStreaming(true);

    abortRef.current = new AbortController();

    let accumulated = "";
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`/api/chat/${statementId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userMessage: trimmed, dealerName, lenderName }),
        signal: abortRef.current.signal,
        credentials: "include",
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ message: t("floorplan:chat.requestFailed") }));
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", content: `${t("common:error")}: ${errData.message}` };
          return updated;
        });
        return;
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.error) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: `${t("common:error")}: ${parsed.error}` };
                return updated;
              });
              break;
            }
            if (parsed.content) {
              accumulated += parsed.content;
              const snapshot = accumulated;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: snapshot };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: "assistant",
            content: `${t("common:error")}: ${err.message ?? t("floorplan:chat.genericError")}`,
          };
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  return (
    <div className="rounded-lg border overflow-hidden" data-testid="receivable-chat">
      {/* Compliance banner */}
      <div className="flex items-start gap-2 px-3 py-2.5 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-700/40">
        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
        <p className="text-xs text-yellow-800 dark:text-yellow-300 leading-relaxed flex-1" data-testid="chat-disclaimer-banner">
          <span className="font-semibold">{t("floorplan:chat.assistantTitle")}</span> — {t("floorplan:chat.disclaimer")}
        </p>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clearChat}
            disabled={isStreaming}
            className="shrink-0 text-yellow-700 dark:text-yellow-400 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t("floorplan:chat.clearHistory")}
            data-testid="chat-clear-button"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Message area */}
      <div
        ref={scrollRef}
        className="h-64 overflow-y-auto p-3 space-y-3 bg-background"
        data-testid="chat-message-area"
      >
        {isLoadingHistory ? (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
            <Bot className="w-8 h-8 text-muted-foreground/40 animate-pulse" />
            <p className="text-xs text-muted-foreground">{t("floorplan:chat.loadingConversation")}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-center">
            <Bot className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground">
              {t("floorplan:chat.emptyLine1")}<br />{t("floorplan:chat.emptyLine2")}
            </p>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              ref={
                msg.role === "user" && i === messages.length - 2 && isStreaming
                  ? lastUserBubbleRef
                  : msg.role === "assistant" && i === messages.length - 1 && isStreaming
                    ? streamingBubbleRef
                    : undefined
              }
              className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-message-${msg.role}-${i}`}
            >
              {msg.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {msg.content}
                {isStreaming && i === messages.length - 1 && msg.role === "assistant" && (
                  <span className="inline-block w-1 h-3 ml-0.5 bg-current opacity-70 animate-pulse" />
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              )}
            </div>
          ))
        )}

        {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex gap-2 justify-start" data-testid="chat-thinking">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Bot className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="bg-muted rounded-xl px-3 py-2 text-xs text-muted-foreground animate-pulse">
              {t("floorplan:chat.thinking")}
            </div>
          </div>
        )}
      </div>

      {/* Starter chips */}
      {!isLoadingHistory && messages.length === 0 && (
        <div className="px-3 pb-2 pt-1 border-t border-border/40 bg-muted/20">
          <div className="flex flex-wrap gap-1.5">
            {STARTER_QUESTIONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => sendMessage(q)}
                disabled={isStreaming}
                className="text-xs px-2.5 py-1 rounded-full border border-border bg-background hover:bg-muted hover:border-primary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`chat-starter-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input row */}
      <div className="flex gap-2 px-3 py-2.5 border-t bg-background">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("floorplan:chat.inputPlaceholder")}
          disabled={isStreaming || isLoadingHistory}
          className="text-xs h-8"
          data-testid="chat-input"
        />
        <Button
          size="sm"
          className="h-8 w-8 p-0 shrink-0"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || isStreaming || isLoadingHistory}
          data-testid="chat-send-button"
        >
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
